<template>
    <div class="relative form-control w-full max-w-xs" ref="tagContainer">
        <label class="input input-bordered input-sm flex items-center gap-2 flex-wrap min-h-[2rem] py-1">
            <!-- 显示已有的tags -->
            <div v-for="(tag, index) in tags" :key="'tag-' + index" 
                 class="badge badge-ghost badge-sm gap-1 flex items-center">
                <span>{{ tag }}</span>
                <svg @click.prevent.stop="removeTag(index)" 
                     class="fill-current h-3 w-3 cursor-pointer hover:fill-red-500" 
                     viewBox="0 0 1024 1024">
                    <path d="M512 85.333333C276.352 85.333333 85.333333 276.352 85.333333 512s191.018667 426.666667 426.666667 426.666667 426.666667-191.018667 426.666667-426.666667S747.648 85.333333 512 85.333333zM671.061333 662.848c-19.242667 19.221333-50.410667 19.221333-69.632 0l-81.216-81.216-81.216 81.216c-19.242667 19.221333-50.410667 19.221333-69.632 0-19.221333-19.242667-19.221333-50.410667 0-69.632L450.581333 512l-81.237333-81.216c-19.221333-19.242667-19.221333-50.410667 0-69.632 19.242667-19.221333 50.410667-19.221333 69.632 0l81.216 81.216 81.216-81.216c19.242667-19.221333 50.410667-19.242667 69.632 0 19.221333 19.242667 19.221333 50.410667 0 69.632L589.824 512l81.237333 81.216C690.282667 612.458667 690.304 643.626667 671.061333 662.848z"></path>
                </svg>
            </div>
            
            <!-- 输入框 -->
            <input
                type="text"
                v-model="inputValue"
                class="grow min-w-[60px] border-none outline-none bg-transparent"
                :placeholder="tags.length === 0 ? placeholder : ''"
                @keyup.enter="handleEnter"
                @keyup.backspace="handleBackspace"
                @click="toggleDropdown"
                @blur="handleBlur"
                ref="inputField"
            />
            
            <!-- 清空按钮 -->
            <svg t="1727684299243" 
                 v-show="tags.length > 0 || inputValue" 
                 @click.prevent.stop="clearAll" 
                 class="fill-gray-300 h-4 w-4 cursor-pointer hover:fill-red-500" 
                 viewBox="0 0 1024 1024">
                <path d="M512 85.333333C276.352 85.333333 85.333333 276.352 85.333333 512s191.018667 426.666667 426.666667 426.666667 426.666667-191.018667 426.666667-426.666667S747.648 85.333333 512 85.333333zM671.061333 662.848c-19.242667 19.221333-50.410667 19.221333-69.632 0l-81.216-81.216-81.216 81.216c-19.242667 19.221333-50.410667 19.221333-69.632 0-19.221333-19.242667-19.221333-50.410667 0-69.632L450.581333 512l-81.237333-81.216c-19.221333-19.242667-19.221333-50.410667 0-69.632 19.242667-19.221333 50.410667-19.221333 69.632 0l81.216 81.216 81.216-81.216c19.242667-19.221333 50.410667-19.242667 69.632 0 19.221333 19.242667 19.221333 50.410667 0 69.632L589.824 512l81.237333 81.216C690.282667 612.458667 690.304 643.626667 671.061333 662.848z"></path>
            </svg>
        </label>
        
        <!-- 下拉建议列表 -->
        <div v-if="showDropdown && optionsShow.length > 0"
             :class="[
                'absolute left-0 z-10 menu p-2 shadow-lg bg-base-100 rounded-lg w-full border border-solid max-h-[200px] overflow-y-auto',
                dropdownDirection === 'up' ? 'bottom-full' : 'top-full'
            ]">
            <div
                v-for="(item, index) in optionsShow"
                :key="'option-' + index"
                :class="['p-2', 'cursor-pointer', 'hover:bg-base-200', 'hover:rounded-lg']"
                @click="selectOption(item)"
            >
                <div class="flex justify-between">
                    <div>{{ item.name || item }}</div>
                    <div v-if="isSelected(item)" class="fill-current text-primary">✓</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'SingleTag',
    props: {
        // 值为字符串数组
        value: {
            type: Array,
            default: () => []
        },
        // 选项列表，支持字符串数组或对象数组
        options: {
            type: Array,
            default: () => []
        },
        // 占位符
        placeholder: {
            type: String,
            default: '请输入标签，按回车添加'
        },
        // 分隔符，用于输入时自动分割
        separator: {
            type: String,
            default: ','
        },
        // 是否允许重复标签
        allowDuplicates: {
            type: Boolean,
            default: false
        },
        // 最大标签数量
        maxTags: {
            type: Number,
            default: 0
        },
        // 是否过滤已选中的选项
        filterSelected: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            inputValue: '',
            showDropdown: false,
            dropdownDirection: 'down',
            isSelecting: false
        };
    },
    computed: {
        // 当前的标签列表
        tags: {
            get() {
                return this.value || [];
            },
            set(newTags) {
                this.$emit('input', newTags);
            }
        },
        // 过滤后的选项列表
        optionsShow() {
            if (!this.inputValue.trim()) {
                return this.filteredOptions;
            }
            
            // 根据输入值过滤选项
            return this.filteredOptions.filter(option => {
                const optionText = typeof option === 'string' ? option : option.name;
                return optionText.toLowerCase().includes(this.inputValue.toLowerCase());
            });
        },
        // 过滤已选中的选项
        filteredOptions() {
            if (!this.filterSelected) {
                return this.options;
            }
            
            return this.options.filter(option => {
                const optionValue = typeof option === 'string' ? option : option.value || option.name;
                return !this.tags.includes(optionValue);
            });
        }
    },
    mounted() {
        document.addEventListener('click', this.handleClickOutside);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside);
        clearTimeout(this.blurTimeout);
    },
    methods: {
        // 处理点击外部区域
        handleClickOutside(event) {
            const container = this.$refs.tagContainer;
            if (container && !container.contains(event.target)) {
                this.showDropdown = false;
            }
        },
        
        // 切换下拉菜单显示状态
        toggleDropdown() {
            if (this.options.length > 0) {
                this.showDropdown = !this.showDropdown;
                if (this.showDropdown) {
                    this.$nextTick(() => {
                        this.adjustDropdownPosition();
                    });
                }
            }
        },
        
        // 调整下拉菜单位置
        adjustDropdownPosition() {
            const inputElement = this.$el.querySelector('input');
            const dropdownElement = this.$el.querySelector('.menu');
            if (!dropdownElement) return;
            
            const viewportHeight = window.innerHeight;
            const { top, bottom } = inputElement.getBoundingClientRect();
            const spaceBelow = viewportHeight - bottom;
            const spaceAbove = top;

            if (spaceBelow < dropdownElement.offsetHeight && spaceAbove > dropdownElement.offsetHeight) {
                this.dropdownDirection = 'up';
            } else {
                this.dropdownDirection = 'down';
            }
        },
        
        // 处理回车键
        handleEnter() {
            if (this.inputValue.trim()) {
                this.addTag(this.inputValue.trim());
            }
        },

        // 处理失去焦点
        handleBlur() {
            // 延迟执行,避免与下拉选项点击冲突
            setTimeout(() => {
                if (this.inputValue.trim() && !this.isSelecting) {
                    this.addTag(this.inputValue.trim());
                }
            }, 200);
        },
        
        // 处理退格键
        handleBackspace() {
            if (this.inputValue === '' && this.tags.length > 0) {
                this.removeTag(this.tags.length - 1);
            }
        },
        
        // 添加标签
        addTag(tagValue) {
            // 检查是否包含分隔符，如果有则分割
            const values = tagValue.split(this.separator).map(v => v.trim()).filter(v => v);
            
            values.forEach(value => {
                if (this.canAddTag(value)) {
                    const newTags = [...this.tags, value];
                    this.tags = newTags;
                    this.$emit('change', newTags);
                    this.$emit('tag-added', value);
                }
            });
            
            this.inputValue = '';
            this.showDropdown = false;
        },
        
        // 检查是否可以添加标签
        canAddTag(value) {
            // 检查最大数量限制
            if (this.maxTags > 0 && this.tags.length >= this.maxTags) {
                this.$emit('error', `最多只能添加${this.maxTags}个标签`);
                return false;
            }
            
            // 检查重复
            if (!this.allowDuplicates && this.tags.includes(value)) {
                this.$emit('error', '不能添加重复的标签');
                return false;
            }
            
            return true;
        },
        
        // 移除标签
        removeTag(index) {
            const removedTag = this.tags[index];
            const newTags = this.tags.filter((_, i) => i !== index);
            this.tags = newTags;
            this.$emit('change', newTags);
            this.$emit('tag-removed', removedTag);
        },
        
        // 清空所有标签
        clearAll() {
            this.inputValue = '';
            this.tags = [];
            this.showDropdown = false;
            this.$emit('change', []);
            this.$emit('cleared');
            this.$refs.inputField.focus();
        },
        
        // 从下拉选项中选择
        selectOption(option) {
            this.isSelecting = true;
            const value = typeof option === 'string' ? option : option.value || option.name;
            this.addTag(value);
            setTimeout(() => {
                this.isSelecting = false;
            }, 300);
        },
        
        // 检查选项是否已选中
        isSelected(option) {
            const value = typeof option === 'string' ? option : option.value || option.name;
            return this.tags.includes(value);
        }
    }
};
</script>

<style scoped>
.badge {
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
    margin: 0.125rem 0;
    height: 1.5rem;
    line-height: 1;
}

.badge svg {
    transition: fill 0.2s ease;
}

.input {
    flex-wrap: wrap;
    align-items: center;
    min-height: 2rem;
    height: auto;
    padding: 0.25rem 0.5rem;
    gap: 0.25rem;
}

.input input {
    flex: 1;
    min-width: 60px;
    background: transparent;
    border: none;
    outline: none;
    line-height: 1.5rem;
    height: 1.5rem;
    align-self: center;
}

.menu.top-full {
    top: 100%;
    margin-top: 2px;
}

.menu.bottom-full {
    bottom: 100%;
    margin-bottom: 2px;
}

/* 响应式调整 */
@media (max-width: 640px) {
    .form-control {
        max-width: 100%;
    }
}
</style>
